// THE PRE-SPEND PURCHASE NOTICE: WHEN THE WEBSITE ASKS, WHEN IT SHOWS, AND THE OWNER'S ANSWER (LAUNCH-PF,
// R244; R182; docs/PURCHASE_FIRST.md, "The website").
//
// THE SERVER DECIDES WHETHER THERE IS A NOTICE (`v2ResolveProperty` op `purchaseNotice`, functions/src/v2/resolve/
// prespendPurchase.ts, which calls the app's one writer). This module decides only what the app's `PurchaseNoticeGate`
// decides (AppealAce/Services/PurchaseNotice.swift), each a port of one of its functions and named for it:
//   - ASK ONCE PER APPEAL AND ADDRESS, only while the server says the switch is on (`looksUpBeforeSpend`; the app's
//     `purchaseNoticeLookedUpFor`), and never wait longer than the app's eight seconds (`lookupDeadlineSeconds`,
//     `notice(from:within:)`). ANY failure — a refusal, an outage, the limit, the deadline — is no notice: today's path,
//     and the details step's NEW-7 is the check, as the app's details card is.
//   - THE ORDER (`nextStep`): the address first (the compass sheet, which the page settles before asking), then the
//     notice while it is unanswered, then the credit sheet. With no notice — or one answered — today's order exactly.
//   - THE LAST CHECK BEFORE THE SPEND (`spendAllowed`): false while a notice stands unanswered, whatever route reached
//     the spend.
//   - WHAT A CHOICE DOES (`answer(for:notice:)`): "Go back" is no answer — nothing is spent and the notice shows again
//     next time; each of the three routes is an answer, carried into the appeal the credit opens.
//   - THE ANSWER AFTER THE SPEND (`keepingAnswer`, and the details card's own grounds route): "That wasn't an
//     open-market sale" becomes the details step's own explicit answer, `lastSaleDistressed: true`, ON THE SAME SALE the
//     notice named and no other; "My home has a condition or record problem" opens the details step with that route
//     already chosen. "That sale isn't right" carries nothing: the figure is corrected on the details step, as the app's is.
//
// HELD IN THIS TAB'S MEMORY, per appeal, like the letter's attempt keys (./letterAttempt.ts): it outlives the page being
// left and opened again, and dies with a reload — after which the details step's card asks again, which is the app's
// backstop. Registered in docs/THIN_CLIENT_DEBT.md beside the app's own row for the same functions.

import type { PurchaseNotice, SubjectView, V2Result } from "@contract";
import type { Transport } from "../transport/types";

/** `PurchaseNoticeGate.lookupDeadlineSeconds`: past this, the owner goes on without a notice. */
export const PURCHASE_NOTICE_DEADLINE_MS = 8_000;

/** `PurchaseNoticeChoice`. */
export type PurchaseNoticeChoice = "goBack" | "saleIsntRight" | "notOpenMarket" | "otherGrounds";

/** `CarriedPurchaseAnswer`: the owner's answer, with the sale it was about. */
export interface CarriedPurchaseAnswer {
  readonly answer: Exclude<PurchaseNoticeChoice, "goBack">;
  readonly purchase: number;
  readonly saleDate: string | null;
}

interface Held {
  /** What the notice was asked for: the appeal and the address its credit would be spent on. */
  readonly lookedUpFor: string;
  readonly notice: PurchaseNotice | null;
  answer: CarriedPurchaseAnswer | null;
}

const held = new Map<string, Held>();

/** WHAT THE NOTICE WAS ASKED FOR: the address the credit would be spent on AND THE UNIT IN FORCE — the app keys on
 *  `subject.lookupAddress`, which folds the unit in (Models.swift `lookupAddress`), so a condo owner who changes their
 *  unit on the same appeal is asked about the new parcel, never told the old one's answer (LAUNCH-PF round 2, P2.1). */
const keyOf = (address: string | null, unit: string | null): string => `${(address ?? "").trim()}\u001f${(unit ?? "").trim()}`;

/** The notice and answer this tab holds for the appeal, whatever address it was asked for. */
export function heldPurchaseNotice(appealId: string): { notice: PurchaseNotice | null; answer: CarriedPurchaseAnswer | null } | null {
  const h = held.get(appealId);
  return h === undefined ? null : { notice: h.notice, answer: h.answer };
}

/** The owner's answer for the appeal, for the details step. */
export function heldPurchaseAnswer(appealId: string): CarriedPurchaseAnswer | null {
  return held.get(appealId)?.answer ?? null;
}

/** A result, or null when it does not arrive within `ms` — a race, as the app's is, so a slow answer never holds the
 *  owner past the deadline. */
export function within<T>(work: Promise<T>, ms: number): Promise<T | null> {
  return new Promise((resolve) => {
    const timer = setTimeout(() => resolve(null), ms);
    work.then(
      (value) => {
        clearTimeout(timer);
        resolve(value);
      },
      () => {
        clearTimeout(timer);
        resolve(null);
      },
    );
  });
}

/**
 * `lookUpPurchaseNoticeIfOn`: the server's notice for this appeal, asked at most once per address and unit, and only while
 * the switch is on. Off: nothing is asked and the answer is null — today. A new address or unit forgets the old notice
 * and answer.
 */
export async function lookUpPurchaseNotice(
  transport: Transport, on: boolean, appealId: string, address: string | null, unit: string | null,
  deadlineMs: number = PURCHASE_NOTICE_DEADLINE_MS,
): Promise<PurchaseNotice | null> {
  if (!on) return null;
  const lookedUpFor = keyOf(address, unit);
  const h = held.get(appealId);
  if (h !== undefined && h.lookedUpFor === lookedUpFor) return h.notice;
  held.delete(appealId);
  // A transport that THROWS rather than answering is read as a failure too: the call is made inside a promise, so a
  // synchronous throw becomes a rejection, which `within` reads as no notice (LAUNCH-PF round 2).
  const result: V2Result<{ body: { purchaseNotice: PurchaseNotice | null } }> | null =
    await within(Promise.resolve().then(() => transport.v2ResolveProperty.purchaseNotice({ appealId })), deadlineMs);
  const notice = result !== null && result.ok ? result.response.body.purchaseNotice : null;
  held.set(appealId, { lookedUpFor, notice, answer: null });
  return notice;
}

/** `PurchaseNoticeGate.nextStep`, after the address is settled: the notice while it stands unanswered, else the credit
 *  sheet. */
export function nextPreSpendSheet(notice: PurchaseNotice | null, answer: CarriedPurchaseAnswer | null): "purchase" | "confirm" {
  return notice !== null && answer === null ? "purchase" : "confirm";
}

/** `PurchaseNoticeGate.spendAllowed`: false while a notice stands unanswered. */
export function spendAllowed(notice: PurchaseNotice | null, answer: CarriedPurchaseAnswer | null): boolean {
  return notice === null || answer !== null;
}

/** `PurchaseNoticeGate.answer(for:notice:)`: "Go back" is null; each route is an answer about the notice's own sale. */
export function answerFor(choice: PurchaseNoticeChoice, notice: PurchaseNotice): CarriedPurchaseAnswer | null {
  if (choice === "goBack") return null;
  return { answer: choice, purchase: notice.purchase.amount, saleDate: notice.saleDate };
}

/** The owner's choice on the notice, kept for the appeal. "Go back" keeps nothing: the notice stands. */
export function recordPurchaseChoice(appealId: string, choice: PurchaseNoticeChoice): CarriedPurchaseAnswer | null {
  const h = held.get(appealId);
  if (h === undefined || h.notice === null) return null;
  h.answer = answerFor(choice, h.notice);
  return h.answer;
}

/** `PurchaseNoticeGate.keepingAnswer`, as the details step needs it: whether the owner's "not open-market" is about THIS
 *  sale — the price the notice named, and its date where it named one. A different sale is a different fact. */
export function keepsNotOpenMarket(answer: CarriedPurchaseAnswer | null, sale: SubjectView["lastSale"]): boolean {
  if (answer === null || answer.answer !== "notOpenMarket" || sale === null) return false;
  return sale.price.amount === answer.purchase && (answer.saleDate === null || sale.date === answer.saleDate);
}

/** Test-only, and a fresh tab: nothing held. */
export function forgetPurchaseNotices(): void {
  held.clear();
}
